import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { isDebug } from '../utils/urlParams'
import { getItem, setItem, removeItem } from '../utils/storage'

export const useDebugStore = defineStore('debug', () => {
    const statLevel = ref(0)
    const targetBitrate = ref(2_500_000)

    function cycleStatLevel(): number {
        statLevel.value = statLevel.value === 0 ? 1 : 0
        return statLevel.value
    }

    function setStatLevel(level: number) {
        statLevel.value = level
    }

    function adjustBitrate(direction: 'up' | 'down') {
        const s = bitrateStep(targetBitrate.value)
        if (direction === 'up') {
            targetBitrate.value += s
        } else if (s <= targetBitrate.value) {
            targetBitrate.value -= s
        }
    }

    const DEBUG_KEY = 'DEBUG_ENABLED'
    const debugEnabled = ref(isDebug() || getItem(DEBUG_KEY) === 'true')

    // Инициализация statLevel при старте, если debug включён
    if (debugEnabled.value) {
        statLevel.value = 1
    }

    // Персистить debug=true из URL в localStorage
    if (isDebug() && getItem(DEBUG_KEY) !== 'true') {
        setItem(DEBUG_KEY, 'true')
    }

    function setDebugEnabled(value: boolean) {
        debugEnabled.value = value
        if (value) {
            setItem(DEBUG_KEY, 'true')
            statLevel.value = 1
        } else {
            removeItem(DEBUG_KEY)
            statLevel.value = 0
        }
    }

    const JITTER_BUFFER_ENABLED_KEY = 'JITTER_BUFFER_ENABLED'
    const JITTER_BUFFER_MS_KEY = 'JITTER_BUFFER_MS'
    const jitterBufferEnabled = ref(getItem(JITTER_BUFFER_ENABLED_KEY) === 'true')
    const jitterBufferMs = ref(Number(getItem(JITTER_BUFFER_MS_KEY)) || 4000)

    function setJitterBufferEnabled(value: boolean) {
        jitterBufferEnabled.value = value
        if (value) {
            setItem(JITTER_BUFFER_ENABLED_KEY, 'true')
        } else {
            removeItem(JITTER_BUFFER_ENABLED_KEY)
        }
    }

    function setJitterBufferMs(value: number) {
        const clamped = Math.max(0, Math.min(4000, value))
        jitterBufferMs.value = clamped
        setItem(JITTER_BUFFER_MS_KEY, String(clamped))
    }

    const jitterBufferTarget = computed<number | null>(() =>
        jitterBufferEnabled.value ? jitterBufferMs.value : null
    )

    return {
        statLevel, targetBitrate, cycleStatLevel, setStatLevel, adjustBitrate, debugEnabled, setDebugEnabled,
        jitterBufferEnabled, jitterBufferMs, setJitterBufferEnabled, setJitterBufferMs, jitterBufferTarget,
    }
})

function bitrateStep(bitrate: number): number {
    if (bitrate > 1_000_000) return 250_000
    if (bitrate > 500_000)   return 100_000
    if (bitrate > 100_000)   return 50_000
    return 10_000
}
